import {APIClient} from '../../../utils/APIClient';
import {API_URL, APP_URL, LINKEDIN_AUTHORIZATION_URL, TIKTOK_APP_ID} from '../../../settings/local_settings';
import {
    ANALYTICS_CHANNEL, DV360_CHANNEL, ESCALAFY_CHANNEL, FACEBOOK_CHANNEL, GOOGLE_CHANNEL, LINKEDIN_CHANNEL,
    MERCADO_ADS_CHANNEL, SEARCH_CONSOLE_CHANNEL, TIKTOK_CHANNEL,
} from '../../channels/channels';
import {setPendingLinkedinReconnect} from './pendingLinkedinReconnect';
import {forgetReconnect, rememberReconnect} from './reconnectNote';
import {escalafyConnectErrorKey, startEscalafyAgencyOAuth} from './EscalafyConnectButton';
import {startMercadoAdsAgencyAuth} from '../../channels/mercado_ads/mercadoAdsApi';
import {errorViewOf} from '../../channels/mercado_ads/mercadoAds';

/**
 * «Reconectar» un acceso de la agencia: el mecanismo de cada canal, sin React.
 *
 * Es el `handleReconnect` de `CredentialRow.js` llevado a un módulo para que lo use Plataformas en la
 * 2.1 (tanda «Agencia 2.1», contrato §2). `CredentialRow` no se tocó: el clásico sigue con su copia, y
 * la duplicación está anotada en el handoff del frente. Si cambia el mecanismo de un canal, cambia en
 * los dos lados.
 *
 * Cada canal reconecta distinto, y no por gusto:
 * - Google, GA4, Search Console y DV360 piden la URL del OAuth al backend y se van de página.
 * - TikTok y LinkedIn arman la URL acá. LinkedIn deja anotado el id de ESTA conexión (15 min, un uso),
 *   porque quien autoriza puede ser otra persona y el nombre solo no alcanza para reconocerla.
 * - Meta reconecta con el SDK de Facebook (popup), que necesita un botón montado: abre un modal.
 * - Escalafy usa el OAuth de la agencia con PKCE, el mismo de Economía.
 * - Mercado Ads pide la URL al backend (`get_auth_url/mercado_ads/`, con el `state` del flujo de la
 *   agencia) y, si no puede ir, dice por qué con el texto de su error.
 *
 * Una diferencia con el clásico, a propósito: allá la tabla busca la URL de Search Console con la
 * clave `'SEARCH_CONSOLE'`, pero el canal se llama `'GSC'` (`SEARCH_CONSOLE_CHANNEL`), así que su
 * «Reconectar» no hace nada. Acá va por la constante.
 *
 * Resuelve `{ok: true}` cuando ya se fue (o se va) a la plataforma, y `{ok: false, errorKey}` si no
 * pudo: `errorKey` es la clave i18n del aviso, o `null` para que la pantalla use su genérico.
 */

const OAUTH_AUTH_URL_BY_CHANNEL = {
    [GOOGLE_CHANNEL]: 'get_auth_url/',
    [ANALYTICS_CHANNEL]: 'get_auth_url/ga4/',
    [SEARCH_CONSOLE_CHANNEL]: 'get_auth_url/gsc/',
    [DV360_CHANNEL]: 'get_auth_url/dv360/',
    [MERCADO_ADS_CHANNEL]: 'get_auth_url/mercado_ads/',
};

const RECONNECTABLE_CHANNELS = [
    ...Object.keys(OAUTH_AUTH_URL_BY_CHANNEL), TIKTOK_CHANNEL, LINKEDIN_CHANNEL, FACEBOOK_CHANNEL, ESCALAFY_CHANNEL,
];

// En Meta el nombre no se toca al reconectar (lo elige la agencia, no el perfil con el que se
// re-autoriza): la conexión viaja por `reconnect_acc_id`. LinkedIn lo lleva en su nota y Escalafy la
// reconoce por `reconnect_credential_id`.
// Mercado Ads tampoco: la reconoce el backend por el usuario de Mercado Libre que autoriza.
const CHANNELS_WITHOUT_PENDING_LABEL = [FACEBOOK_CHANNEL, LINKEDIN_CHANNEL, ESCALAFY_CHANNEL, MERCADO_ADS_CHANNEL];

// Google, TikTok y DV360 reconocían la conexión a reconectar sólo por el nombre, y el nombre pasó a ser
// opcional (tanda «Reconexión de pauta», contrato §2.1): su id viaja en la nota de `reconnectNote` y el
// callback lo manda como `reconnect_credential_id`. El nombre se sigue mandando, para un backend viejo.
export const CHANNELS_WITH_CREDENTIAL_NOTE = [GOOGLE_CHANNEL, TIKTOK_CHANNEL, DV360_CHANNEL];

/** Anota qué conexión se renueva, o borra una nota vieja (un «Reconectar» de cuenta abandonado). */
export function rememberCredentialReconnect(channel, credential) {
    if (CHANNELS_WITH_CREDENTIAL_NOTE.includes(channel) && credential && credential.id != null) {
        rememberReconnect(channel, {credentialId: credential.id});
    } else {
        forgetReconnect();
    }
}

// Los canales en los que una conexión que hizo el cliente (desde su portal, o con el link de Mercado Ads)
// la vuelve a autorizar él, no la agencia.
const CLIENT_RECONNECTS_CHANNELS = [ESCALAFY_CHANNEL, MERCADO_ADS_CHANNEL];

const FAILED = {ok: false, errorKey: null};

function goTo(url) {
    window.location.assign(url);
}

/**
 * ¿La agencia puede reconectar este acceso? Una conexión de Escalafy o de Mercado Ads que hizo el
 * cliente (su portal, o el link para el vendedor) la reconecta él: el OAuth de la agencia crearía otra
 * credencial, de la agencia, con el token del dueño (mismo criterio que `CredentialRow`).
 */
export function canReconnectCredential(channel, credential) {
    if (!RECONNECTABLE_CHANNELS.includes(channel)) return false;
    return !(CLIENT_RECONNECTS_CHANNELS.includes(channel) && credential && credential.managed_by_client);
}

function rememberLabel(channel, credential) {
    if (!credential.label || CHANNELS_WITHOUT_PENDING_LABEL.includes(channel)) return;
    try {
        localStorage.setItem('pending_credential_label', credential.label);
    } catch (error) {
        // Sin localStorage la reconexión sale igual: el backend le pone el nombre por default.
    }
}

/**
 * @param channel   el canal del acceso (constante de `channels.js`).
 * @param credential la credencial tal cual la manda `GET agency/connections/`.
 * @param options.navigate   cómo irse de página (los tests lo reemplazan).
 * @param options.onFacebook si viene, Meta abre su modal acá mismo con el id de la conexión; sin él,
 *                           navega a `?reconnect_pending=FACEBOOK`, como el clásico.
 */
export function reconnectCredential(channel, credential, {navigate = goTo, onFacebook = null} = {}) {
    if (!credential || !canReconnectCredential(channel, credential)) return Promise.resolve(FAILED);

    rememberLabel(channel, credential);
    rememberCredentialReconnect(channel, credential);

    if (channel === ESCALAFY_CHANNEL) {
        // `credential_id` es la `EscalafyCredentials` (la que reconecta el OAuth de Escalafy); `id`, la
        // credencial genérica de la fila.
        return startEscalafyAgencyOAuth({reconnectCredentialId: credential.credential_id})
            .then((result) => (result.ok ? {ok: true} : {ok: false, errorKey: escalafyConnectErrorKey(result.response)}))
            .catch(() => ({ok: false, errorKey: escalafyConnectErrorKey(null)}));
    }

    if (channel === MERCADO_ADS_CHANNEL) {
        return startMercadoAdsAgencyAuth({navigate})
            .then((result) => (result.ok ? {ok: true} : {ok: false, errorKey: errorViewOf(result.code).textKey}));
    }

    if (OAUTH_AUTH_URL_BY_CHANNEL[channel]) {
        return new APIClient(API_URL + OAUTH_AUTH_URL_BY_CHANNEL[channel]).call('GET')
            .then((data) => {
                // Sin URL no hay a dónde ir: el clásico asignaba `undefined` a la ubicación.
                if (!data || !data.auth_url) return FAILED;
                navigate(data.auth_url);
                return {ok: true};
            })
            .catch(() => FAILED);
    }

    if (channel === TIKTOK_CHANNEL) {
        const redirectUri = encodeURIComponent(`${APP_URL}connect-tiktok`);
        navigate(`https://business-api.tiktok.com/portal/auth?app_id=${TIKTOK_APP_ID}&state=your_custom_params&redirect_uri=${redirectUri}`);
        return Promise.resolve({ok: true});
    }

    if (channel === LINKEDIN_CHANNEL) {
        // Reconecta ESTA conexión aunque quien autorice sea otra persona (el nombre solo no alcanza).
        setPendingLinkedinReconnect(credential.id, credential.label);
        navigate(LINKEDIN_AUTHORIZATION_URL);
        return Promise.resolve({ok: true});
    }

    // Meta: el único canal que reconecta con un popup del SDK, desde un modal. Abierto acá mismo, la
    // pantalla no se va: `stays` le dice al botón que no quede ocupado esperando una navegación.
    if (onFacebook) {
        onFacebook(credential.id);
        return Promise.resolve({ok: true, stays: true});
    }
    navigate(`/agency/conections?reconnect_pending=FACEBOOK&reconnect_acc_id=${credential.id}`);
    return Promise.resolve({ok: true});
}
